import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Eye, Bell, Shield, FileCheck, Zap, Clock, 
  CheckCircle, ArrowRight, Smartphone, Search,
  AlertTriangle, Users, Lock, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const HowItWorks = () => {
  return (
    <div className="min-h-screen bg-slate-950 pt-20" data-testid="how-it-works-page">
      {/* Hero */}
      <section className="section-spacing relative">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        
        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-4 py-2 mb-6">
              <Zap className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">Simple & Powerful Protection</span>
            </div>

            <h1 className="text-hero text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How <span className="text-cyan-400">Sozialsetu</span> Works
            </h1>

            <p className="text-body max-w-2xl mx-auto mb-8">
              We combine AI-powered monitoring with real human support and insurance backing. 
              Here's how we keep you protected 24/7.
            </p>

            <div className="flex flex-wrap justify-center gap-6 text-slate-400">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                <span>5 minute setup</span>
              </div>
              <div className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-cyan-400" />
                <span>Works on mobile</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-cyan-400" />
                <span>Insurance included</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="section-spacing bg-slate-900/30" data-testid="steps-section">
        <div className="container-custom">
          <div className="space-y-8">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-base lg:mr-24"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-2 text-center lg:text-left">
                  <span className="text-7xl font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>01</span>
                </div>
                <div className="lg:col-span-2 flex justify-center lg:justify-start">
                  <div className="w-20 h-20 rounded-3xl bg-cyan-400/10 flex items-center justify-center">
                    <Search className="w-10 h-10 text-cyan-400" />
                  </div>
                </div>
                <div className="lg:col-span-8">
                  <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Connect Your Accounts</h3>
                  <p className="text-slate-400 mb-4 leading-relaxed">Link your Instagram, YouTube, and X accounts securely. We use read-only access to monitor your digital presence without posting anything.</p>
                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />Secure OAuth connection
                    </span>
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />No password sharing
                    </span>
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />One-click setup
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card-base lg:ml-24"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-2 text-center lg:text-left">
                  <span className="text-7xl font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>02</span>
                </div>
                <div className="lg:col-span-2 flex justify-center lg:justify-start">
                  <div className="w-20 h-20 rounded-3xl bg-cyan-400/10 flex items-center justify-center">
                    <Eye className="w-10 h-10 text-cyan-400" />
                  </div>
                </div>
                <div className="lg:col-span-8">
                  <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>24/7 AI Monitoring</h3>
                  <p className="text-slate-400 mb-4 leading-relaxed">Our AI scans mentions, comments, tags, and the web for threats. We detect hate speech, impersonation attempts, and reputation risks.</p>
                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />Real-time scanning
                    </span>
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />Multi-platform coverage
                    </span>
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />Pattern recognition
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="card-base lg:mr-24"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-2 text-center lg:text-left">
                  <span className="text-7xl font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>03</span>
                </div>
                <div className="lg:col-span-2 flex justify-center lg:justify-start">
                  <div className="w-20 h-20 rounded-3xl bg-cyan-400/10 flex items-center justify-center">
                    <Bell className="w-10 h-10 text-cyan-400" />
                  </div>
                </div>
                <div className="lg:col-span-8">
                  <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Instant Alerts</h3>
                  <p className="text-slate-400 mb-4 leading-relaxed">Get notified immediately when we spot trouble. You choose how - WhatsApp, email, or app notifications.</p>
                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />WhatsApp alerts
                    </span>
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />Email notifications
                    </span>
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />Priority levels
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Step 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="card-base lg:ml-24"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-2 text-center lg:text-left">
                  <span className="text-7xl font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>04</span>
                </div>
                <div className="lg:col-span-2 flex justify-center lg:justify-start">
                  <div className="w-20 h-20 rounded-3xl bg-cyan-400/10 flex items-center justify-center">
                    <Shield className="w-10 h-10 text-cyan-400" />
                  </div>
                </div>
                <div className="lg:col-span-8">
                  <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Rapid Response</h3>
                  <p className="text-slate-400 mb-4 leading-relaxed">Our team helps you take action. Report fake accounts, document abuse, and initiate takedowns quickly.</p>
                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />Expert guidance
                    </span>
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />Platform reporting
                    </span>
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />Evidence collection
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Step 5 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="card-base lg:mr-24"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-2 text-center lg:text-left">
                  <span className="text-7xl font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>05</span>
                </div>
                <div className="lg:col-span-2 flex justify-center lg:justify-start">
                  <div className="w-20 h-20 rounded-3xl bg-cyan-400/10 flex items-center justify-center">
                    <FileCheck className="w-10 h-10 text-cyan-400" />
                  </div>
                </div>
                <div className="lg:col-span-8">
                  <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Insurance Protection</h3>
                  <p className="text-slate-400 mb-4 leading-relaxed">If things escalate, your insurance kicks in. Coverage for legal fees, income loss, and crisis management.</p>
                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />Legal cost coverage
                    </span>
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />Income protection
                    </span>
                    <span className="inline-flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-full text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />PR crisis support
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Monitor */}
      <section className="section-spacing bg-slate-950" data-testid="monitoring-section">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What We <span className="text-cyan-400">Monitor</span>
            </h2>
            <p className="text-body">
              Comprehensive coverage across all major platforms where your audience lives.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-base group"
            >
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-cyan-400/10 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-400/20 transition-colors">
                  <MessageSquare className="w-7 h-7 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Comment Monitoring</h3>
                  <p className="text-slate-400 leading-relaxed">Track hateful, abusive, or threatening comments across your posts.</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card-base group"
            >
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-cyan-400/10 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-400/20 transition-colors">
                  <Users className="w-7 h-7 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Impersonation Detection</h3>
                  <p className="text-slate-400 leading-relaxed">Find fake accounts using your name, photos, or brand.</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="card-base group"
            >
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-cyan-400/10 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-400/20 transition-colors">
                  <AlertTriangle className="w-7 h-7 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Mention Tracking</h3>
                  <p className="text-slate-400 leading-relaxed">Monitor what people say about you across the internet.</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="card-base group"
            >
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-cyan-400/10 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-400/20 transition-colors">
                  <Lock className="w-7 h-7 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Security Alerts</h3>
                  <p className="text-slate-400 leading-relaxed">Get warned about potential account takeover attempts.</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Platforms */}
          <motion.div 
            className="mt-16 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-slate-500 mb-6">Platforms we protect</p>
            <div className="flex justify-center items-center gap-4 flex-wrap">
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl px-6 py-3 text-slate-300 font-medium">Instagram</div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl px-6 py-3 text-slate-300 font-medium">YouTube</div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl px-6 py-3 text-slate-300 font-medium">X (Twitter)</div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl px-6 py-3 text-slate-300 font-medium">Facebook</div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl px-6 py-3 text-slate-300 font-medium">LinkedIn</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-slate-900/50 relative" data-testid="cta-section">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ready to Get <span className="text-cyan-400">Started</span>?
            </h2>
            <p className="text-body max-w-2xl mx-auto mb-8">
              Set up your protection in 5 minutes. No technical knowledge required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-full px-8 py-6 font-bold text-base transition-all hover:scale-105"
                  data-testid="get-protected-cta"
                >
                  Get Protected Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button 
                  variant="outline"
                  className="bg-slate-800/50 text-white border border-slate-700 hover:border-cyan-400/50 hover:bg-slate-800 rounded-full px-8 py-6 font-medium transition-all"
                  data-testid="view-pricing-cta"
                >
                  View Pricing
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
